import { useEffect, useState, useCallback } from "react";
import { Megaphone, Send, Loader2, Users, Clock, CheckCheck } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";

const TITLE_MAX = 100;
const MESSAGE_MAX = 400;

const TEMPLATES = [
  {
    key: "shift_change",
    label: "Cambio de turno",
    title: "Cambio de turno",
    message: "Hoy hay un cambio de turno. Revisad el tablón antes de empezar, por favor.",
  },
  {
    key: "late_opening",
    label: "Retraso apertura",
    title: "Retraso en la apertura",
    message: "Hoy abrimos con retraso por incidencia. Os aviso en cuanto tengáis que venir.",
  },
  {
    key: "early_close",
    label: "Cierre anticipado",
    title: "Cocina cierra antes",
    message: "Hoy la cocina cierra 30 minutos antes de lo habitual. Gracias por vuestra flexibilidad.",
  },
  {
    key: "stock_urgent",
    label: "Stock urgente",
    title: "Stock urgente",
    message: "Nos falta un producto clave. Si podéis pasar por el super de camino, avisad al grupo.",
  },
  {
    key: "full_house",
    label: "Lleno total",
    title: "Reservas completas",
    message: "Servicio a tope esta noche. Extra atención en sala y cocina. ¡Vamos!",
  },
  {
    key: "thanks",
    label: "Agradecimiento",
    title: "¡Buen trabajo equipo!",
    message: "Gran servicio hoy. Gracias por el esfuerzo — mañana seguimos con la misma energía.",
  },
];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `hace ${days} día${days === 1 ? "" : "s"}`;
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
}

export default function TeamBroadcastCard({ restaurantId, compact = false }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await api.getPushBroadcastHistory(restaurantId);
      setHistory(res.broadcasts || []);
    } catch {
      // silent — history is secondary
    } finally {
      setLoadingHistory(false);
    }
  }, [restaurantId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const canSend = title.trim() && message.trim() && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await api.pushBroadcast(restaurantId, {
        title: title.trim(),
        message: message.trim(),
        scope: "team",
      });
      const { recipients_with_devices, devices_sent, recipients_total } = res;
      if (devices_sent > 0) {
        toast.success(
          `Enviado a ${recipients_with_devices}/${recipients_total} miembros · ${devices_sent} dispositivo${devices_sent === 1 ? "" : "s"}`,
        );
      } else {
        toast.info(
          `Nadie del equipo tiene la app móvil instalada todavía (${recipients_total} miembro${recipients_total === 1 ? "" : "s"}).`,
        );
      }
      setTitle("");
      setMessage("");
      loadHistory();
    } catch (err) {
      toast.error(err.message || "No se pudo enviar el comunicado");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className={compact ? "p-0 border-0 shadow-none space-y-5" : "p-6 space-y-5"} data-testid="team-broadcast-card">
      {!compact && (
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-700" />
            Comunicado al equipo
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Envía un aviso puntual a todos los móviles del equipo con la app instalada.
            Se muestra como notificación push y como alerta al abrir la app.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <Label className="text-sm text-muted-foreground">Plantillas rápidas</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTitle(t.title.slice(0, TITLE_MAX));
                  setMessage(t.message.slice(0, MESSAGE_MAX));
                }}
                className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/50 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-900 transition-colors"
                data-testid={`broadcast-template-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="bcast-title" className="text-sm">Título</Label>
          <Input
            id="bcast-title"
            data-testid="broadcast-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            placeholder="Ej. Cocina cerrará 30 min antes"
            maxLength={TITLE_MAX}
            className="mt-1.5"
          />
          <div className="text-xs text-muted-foreground text-right mt-1">
            {title.length}/{TITLE_MAX}
          </div>
        </div>
        <div>
          <Label htmlFor="bcast-message" className="text-sm">Mensaje</Label>
          <Textarea
            id="bcast-message"
            data-testid="broadcast-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
            placeholder="Explica lo justo. El personal lo leerá en el móvil."
            rows={3}
            maxLength={MESSAGE_MAX}
            className="mt-1.5 resize-none"
          />
          <div className="text-xs text-muted-foreground text-right mt-1">
            {message.length}/{MESSAGE_MAX}
          </div>
        </div>

        <Button
          onClick={handleSend}
          disabled={!canSend}
          className="w-full sm:w-auto"
          data-testid="broadcast-send-btn"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enviando…
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Enviar al equipo
            </>
          )}
        </Button>
      </div>

      {/* History */}
      <div className="pt-4 border-t space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Últimos comunicados
        </div>
        {loadingHistory ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Aún no has enviado ningún comunicado.
          </p>
        ) : (
          <div className="space-y-2">
            {history.map((b) => (
              <div
                key={b.broadcast_id}
                className="p-3 rounded-lg border bg-muted/50/40"
                data-testid={`broadcast-history-${b.broadcast_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{b.title}</div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {b.message}
                    </p>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    <CheckCheck className="w-3 h-3 mr-1" />
                    {b.devices_sent}/{(b.devices_sent || 0) + (b.devices_failed || 0)}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {b.recipients_with_devices}/{b.recipients_total} miembros
                  </span>
                  <span>·</span>
                  <span>{timeAgo(b.sent_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
